import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAvailability } from '../../store/slices/availabilitySlice';

const STATUS_CONFIG = {
  ONLINE:  { bg: 'bg-emerald-50',  text: 'text-emerald-700',  dot: 'bg-emerald-500',  badge: 'Online',  ring: 'ring-emerald-200' },
  BUSY:    { bg: 'bg-amber-50',    text: 'text-amber-700',    dot: 'bg-amber-400',    badge: 'Busy',    ring: 'ring-amber-200' },
  OFFLINE: { bg: 'bg-gray-50',     text: 'text-gray-500',     dot: 'bg-gray-400',     badge: 'Offline', ring: 'ring-gray-200' },
};

export default function AgentsPage() {
  const dispatch = useDispatch();
  const { list: agents } = useSelector(s => s.availability);

  useEffect(() => {
    dispatch(fetchAvailability());
    const interval = setInterval(() => dispatch(fetchAvailability()), 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const online = agents.filter(a => a.status === 'ONLINE');
  const busy = agents.filter(a => a.status === 'BUSY');
  const offline = agents.filter(a => a.status === 'OFFLINE');

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">Site Engineer Availability</h1>
        <p className="text-gray-400 text-sm mt-0.5">Round-robin assignments go to Online engineers only</p>
      </div>

      {/* Summary pills */}
      <div className="flex gap-3">
        <div className="card px-5 py-3 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="font-bold text-emerald-700 text-lg">{online.length}</span>
          <span className="text-gray-400 text-sm">Online</span>
        </div>
        <div className="card px-5 py-3 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="font-bold text-amber-700 text-lg">{busy.length}</span>
          <span className="text-gray-400 text-sm">Busy</span>
        </div>
        <div className="card px-5 py-3 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
          <span className="font-bold text-gray-600 text-lg">{offline.length}</span>
          <span className="text-gray-400 text-sm">Offline</span>
        </div>
      </div>

      {/* Agent cards */}
      <div className="grid grid-cols-3 gap-4">
        {agents.map(agent => {
          const cfg = STATUS_CONFIG[agent.status] || STATUS_CONFIG.OFFLINE;
          const initials = agent.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
          return (
            <div key={agent.user_id} className={`card p-5 ring-1 ${cfg.ring}`}>
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-magenta-100 flex items-center justify-center text-magenta-600 font-bold text-base">
                    {initials}
                  </div>
                  <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${cfg.dot}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-brand-500 truncate">{agent.full_name}</p>
                    {agent.role === 'TEAM_LEAD' && (
                      <span className="text-xs bg-sky-100 text-sky-700 font-semibold px-2 py-0.5 rounded-full flex-shrink-0">Team Lead</span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs truncate">{agent.email}</p>
                  <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {cfg.badge}
                  </span>
                </div>
              </div>
              {agent.locations_data && agent.locations_data.length > 0 && (
                <div className="mt-3 pt-3 border-t border-brand-50 flex items-start gap-1.5">
                  <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  <div className="flex flex-wrap gap-1">
                    {agent.locations_data.map(l => (
                      <span key={l.id} className="text-xs bg-brand-50 border border-brand-100 text-brand-600 font-medium px-1.5 py-0.5 rounded-md">{l.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {agent.role === 'RE' && agent.team_lead_name && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                  <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  <span>Reports to <span className="font-semibold text-sky-600">{agent.team_lead_name}</span></span>
                </div>
              )}
              <div className="mt-2 text-xs text-gray-400 border-t border-brand-50 pt-2">
                Last seen: {new Date(agent.last_seen).toLocaleString('en-IN')}
              </div>
            </div>
          );
        })}
      </div>

      {online.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-4xl mb-3">😴</div>
          <p className="font-semibold text-brand-500">No agents online</p>
          <p className="text-gray-400 text-sm mt-1">New tickets will be created unassigned until an agent comes online.</p>
        </div>
      )}
    </div>
  );
}
